import type { FormSettings as FormSettingsType } from '@/stores/useFormBuilderStore';
import type { Visibility } from '@/types/form';

import styles from './FormSettings.module.css';

interface FormSettingsProps {
    settings: FormSettingsType;
    onUpdate: (partial: Partial<FormSettingsType>) => void;
}

export function FormSettings({ settings, onUpdate }: FormSettingsProps) {
    return (
        <div className={styles.panel}>
            <h3 className={styles.title}>Configuracion</h3>

            {/* Mode */}
            <div className={styles.group}>
                <label className={styles.label}>Modo</label>
                <select
                    className={styles.select}
                    value={settings.mode}
                    onChange={(e) => onUpdate({ mode: e.target.value as 'form' | 'quiz' })}
                >
                    <option value="form">Formulario</option>
                    <option value="quiz">Quiz</option>
                </select>
            </div>

            {/* Visibility */}
            <div className={styles.group}>
                <label className={styles.label}>Visibilidad</label>
                <select
                    className={styles.select}
                    value={settings.visibility}
                    onChange={(e) => onUpdate({ visibility: e.target.value as Visibility })}
                >
                    <option value="public">Publico</option>
                    <option value="unlisted">No listado</option>
                    <option value="private">Privado</option>
                </select>
                <span className={styles.hint}>
                    {settings.visibility === 'public' && 'Aparece en el feed publico'}
                    {settings.visibility === 'unlisted' && 'Solo accesible con el enlace'}
                    {settings.visibility === 'private' && 'Solo tu puedes verlo'}
                </span>
            </div>

            {/* Toggles */}
            <div className={styles.togglesGroup}>
                <ToggleRow
                    label="Requiere inicio de sesion"
                    hint="Los anonimos no pueden responder"
                    checked={settings.requires_auth}
                    onChange={(v) => onUpdate({ requires_auth: v })}
                />
                <ToggleRow
                    label="Una respuesta por usuario"
                    hint="Limita a una respuesta por cuenta"
                    checked={settings.one_response_per_user}
                    onChange={(v) => onUpdate({ one_response_per_user: v })}
                />
                <ToggleRow
                    label="Permitir editar respuesta"
                    hint="El respondente puede modificar su respuesta"
                    checked={settings.allow_edit_response}
                    onChange={(v) => onUpdate({ allow_edit_response: v })}
                />
                <ToggleRow
                    label="Mostrar barra de progreso"
                    hint="Muestra progreso de secciones al respondente"
                    checked={settings.show_progress_bar}
                    onChange={(v) => onUpdate({ show_progress_bar: v })}
                />
                {settings.mode === 'quiz' && (
                    <ToggleRow
                        label="Mezclar opciones"
                        hint="Randomiza el orden de opciones"
                        checked={settings.shuffle_options}
                        onChange={(v) => onUpdate({ shuffle_options: v })}
                    />
                )}
                <ToggleRow
                    label="Recolectar email"
                    hint="Solicita el correo del respondente"
                    checked={settings.collect_email}
                    onChange={(v) => onUpdate({ collect_email: v })}
                />
            </div>

            {/* Confirmation message */}
            <div className={styles.group}>
                <label className={styles.label}>Mensaje de confirmacion</label>
                <textarea
                    className={styles.textarea}
                    value={settings.confirmation_message}
                    onChange={(e) => onUpdate({ confirmation_message: e.target.value })}
                    placeholder="Respuesta registrada"
                    rows={2}
                    maxLength={500}
                />
            </div>

            {/* Close date */}
            <div className={styles.group}>
                <label className={styles.label}>Fecha de cierre (opcional)</label>
                <input
                    type="datetime-local"
                    className={styles.input}
                    value={settings.closes_at}
                    onChange={(e) => onUpdate({ closes_at: e.target.value })}
                />
            </div>

            {/* Theme color */}
            <div className={styles.group}>
                <label className={styles.label}>Color del encabezado</label>
                <div className={styles.colorRow}>
                    <input
                        type="color"
                        className={styles.colorInput}
                        value={settings.theme_color || '#4F46E5'}
                        onChange={(e) => onUpdate({ theme_color: e.target.value })}
                    />
                    <span className={styles.colorValue}>{settings.theme_color || '#4F46E5'}</span>
                    {settings.theme_color && (
                        <button
                            type="button"
                            className={styles.clearColorBtn}
                            onClick={() => onUpdate({ theme_color: '' })}
                        >
                            Restablecer
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ToggleRow({
    label,
    hint,
    checked,
    onChange,
}: {
    label: string;
    hint: string;
    checked: boolean;
    onChange: (v: boolean) => void;
}) {
    return (
        <div className={styles.toggleRow}>
            <div className={styles.toggleInfo}>
                <span className={styles.toggleLabel}>{label}</span>
                <span className={styles.toggleHint}>{hint}</span>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}
                onClick={() => onChange(!checked)}
            />
        </div>
    );
}
